import { BRAND } from "@/config/brand";
import logoImage from "@/assets/regard-de-nature-logo.png";

interface BrandedPreviewProps {
  format: 'feed' | 'story';
  coverImageUrl: string | null;
  visualTitle: string | null;
  visualTitleSize?: number;
  showLogo: boolean;
  showSiteName: boolean;
  text?: string;
  hashtags?: string | null;
}

export function BrandedPreview({
  format,
  coverImageUrl,
  visualTitle,
  visualTitleSize = 48,
  showLogo,
  showSiteName,
  text,
  hashtags,
}: BrandedPreviewProps) {
  const isFeed = format === 'feed';
  const aspectRatio = isFeed ? '1/1' : '9/16';
  const maxWidth = isFeed ? '320px' : '180px';
  
  // Scale font size for preview (original is for 1080px, preview is ~320px for feed)
  const scaledFontSize = isFeed 
    ? `${visualTitleSize * 0.3}px` 
    : `${visualTitleSize * 0.2}px`;
  
  return (
    <div className="space-y-2">
      <div 
        className="relative bg-gradient-to-br from-muted to-muted/50 rounded-lg overflow-hidden border shadow-sm"
        style={{ aspectRatio, maxWidth }}
      >
        {/* Image de fond */}
        {coverImageUrl ? (
          <img 
            src={coverImageUrl} 
            alt="Preview"
            className="absolute inset-0 w-full h-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-primary/20 to-accent/20">
            <span className="text-muted-foreground text-xs">Aucune image</span>
          </div>
        )}
        
        {/* Overlay gradient pour lisibilité */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/30" />
        
        {/* Header branding */}
        {(showLogo || showSiteName) && (
          <div className="absolute top-3 left-3 right-3 flex items-center gap-2">
            {showLogo && (
              <img 
                src={logoImage} 
                alt={BRAND.name.full}
                className="w-8 h-8 rounded-full border-2 border-white/80 shadow-lg"
              />
            )}
            {showSiteName && (
              <div className="flex flex-col">
                <span 
                  className="text-xs font-bold drop-shadow-lg"
                  style={{ 
                    color: BRAND.colors.gold,
                    fontFamily: BRAND.fonts.heading,
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                  }}
                >
                  {BRAND.name.line1}
                </span>
                <span 
                  className="text-xs font-bold drop-shadow-lg"
                  style={{ 
                    color: 'white',
                    fontFamily: BRAND.fonts.heading,
                    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
                  }}
                >
                  {BRAND.name.line2}
                </span>
              </div>
            )}
          </div>
        )}
        
        {/* Titre visuel (centre) */}
        {visualTitle && (
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <h3 
              className="text-center font-bold drop-shadow-lg max-w-[90%] line-clamp-2"
              style={{ 
                color: 'white',
                fontFamily: BRAND.fonts.heading,
                fontSize: scaledFontSize,
                textShadow: '0 2px 8px rgba(0,0,0,0.9)',
              }}
            >
              {visualTitle}
            </h3>
          </div>
        )}
        
        {/* Footer avec URL du site */}
        <div className="absolute bottom-3 left-3 right-3">
          <p 
            className="text-center text-xs drop-shadow-lg truncate"
            style={{ 
              color: BRAND.colors.gold,
              fontFamily: BRAND.fonts.body,
              textShadow: '0 1px 3px rgba(0,0,0,0.8)',
            }}
          >
            www.regard-de-nature.fr
          </p>
        </div>
        
        {/* Badge format */}
        <div 
          className="absolute top-2 right-2 px-2 py-0.5 rounded text-[10px] font-medium"
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.6)',
            color: 'white',
          }}
        >
          {isFeed ? '1080×1080' : '1080×1920'}
        </div>
      </div>
      
      {/* Texte sous l'aperçu */}
      {text && (
        <div className="bg-muted/50 rounded p-3 text-sm space-y-2" style={{ maxWidth }}>
          <p className="line-clamp-4 text-foreground/80">{text}</p>
          {hashtags && (
            <p className="text-xs text-primary">{hashtags}</p>
          )}
        </div>
      )}
    </div>
  );
}
